import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { Switch, Route, Redirect, useHistory } from 'react-router-dom';
import { Layout, Menu, Breadcrumb } from '@arco-design/web-react';
import cs from 'classnames';
import {
  IconDashboard,
  IconList,
  IconSettings,
  IconFile,
  IconApps,
  IconCheckCircle,
  IconExclamationCircle,
  IconUser,
  IconMenuFold,
  IconMenuUnfold,
  IconArchive,
  IconFire,
  IconCalendar,
  IconStar,
  IconBulb,
  IconNav,
  IconGift,
} from '@arco-design/web-react/icon';
import { useSelector } from 'react-redux';
import qs from 'query-string';
import NProgress from 'nprogress';
import Navbar from './components/NavBar';
import Footer from './components/Footer';
import useRoute, { IRoute } from '@/routes';
import { usePermissions } from '@/hooks/usePermissions';
import useLocale from './utils/useLocale';
import getUrlParams from './utils/getUrlParams';
import lazyload from './utils/lazyload';
import { GlobalState } from './store';
import styles from './style/layout.module.less';

const MenuItem = Menu.Item;
const SubMenu = Menu.SubMenu;

const Sider = Layout.Sider;
const Content = Layout.Content;

const ApprovalSettingsPage = lazyload(() => import('./pages/settings/approval'));
const ApprovalTypeSettingsPage = lazyload(
  () => import('./pages/settings/other/approval-process-templates')
);
const TasksCalendarPage = lazyload(() => import('./pages/tasks/calendar'));
const TasksIndexPage = lazyload(() => import('./pages/tasks/index'));
const TaskProjectDetailPage = lazyload(() => import('./pages/tasks/[projectId]'));
const ApprovalCenterDetailPage = lazyload(
  () => import('./pages/approval-center/[id]')
);
const HoursReportDrilldownPage = lazyload(
  () => import('./pages/reports/hours-report/drilldown')
);
const HrbpAttendancePage = lazyload(
  () => import('./pages/dashboard/hrbp-attendance')
);
const DirectorResourcesPage = lazyload(
  () => import('./pages/dashboard/resources')
);

function getIconFromKey(key) {
  // 提取 key 的第一部分（处理 'presales/list' 这样的 key）
  const baseKey = key.split('/')[0];

  switch (baseKey) {
    case 'dashboard':
      return <IconDashboard className={styles.icon} />;
    case 'list':
      return <IconList className={styles.icon} />;
    case 'form':
      return <IconSettings className={styles.icon} />;
    case 'profile':
      return <IconFile className={styles.icon} />;
    case 'visualization':
      return <IconApps className={styles.icon} />;
    case 'result':
      return <IconCheckCircle className={styles.icon} />;
    case 'exception':
      return <IconExclamationCircle className={styles.icon} />;
    case 'user':
      return <IconUser className={styles.icon} />;
    case 'projects':
      return <IconArchive className={styles.icon} />;
    case 'performance':
      return <IconFire className={styles.icon} />;
    case 'tasks':
      return <IconCalendar className={styles.icon} />;
    case 'approval-center':
      return <IconCheckCircle className={styles.icon} />;
    case 'approvals':
      return <IconCheckCircle className={styles.icon} />;
    case 'presales':
      return <IconBulb className={styles.icon} />;
    case 'contributions':
      return <IconStar className={styles.icon} />;
    case 'costs':
      return <IconGift className={styles.icon} />;
    case 'reports':
      return <IconNav className={styles.icon} />;
    case 'settings':
      return <IconSettings className={styles.icon} />;
    default:
      return <div className={styles['icon-empty']} />;
  }
}

function getFlattenRoutes(routes) {
  const res = [];
  function travel(_routes) {
    _routes.forEach((route) => {
      const visibleChildren = (route.children || []).filter(
        (child) => !child.ignore
      );
      if (route.key && (!route.children || !visibleChildren.length)) {
        try {
          route.component = lazyload(() => import(`./pages/${route.key}`));
          res.push(route);
        } catch (e) {
          console.error(e);
        }
      }
      if (route.children && route.children.length) {
        travel(route.children);
      }
    });
  }
  travel(routes);
  return res;
}

function PageLayout() {
  const urlParams = getUrlParams();
  const history = useHistory();
  const pathname = history.location.pathname;
  const search = history.location.search;
  const currentComponent = qs.parseUrl(pathname).url.slice(1);
  const locale = useLocale();
  const { settings } = useSelector(
    (state: GlobalState) => state
  );

  // 使用 usePermissions hook 获取权限
  const { permissions, loading: permissionsLoading } = usePermissions();

  const [routes, defaultRoute] = useRoute(permissions, permissionsLoading);
  const defaultSelectedKeys = [currentComponent || defaultRoute];
  const paths = (currentComponent || defaultRoute).split('/');
  const defaultOpenKeys = paths.slice(0, paths.length - 1);

  const [breadcrumb, setBreadCrumb] = useState([]);
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const [selectedKeys, setSelectedKeys] =
    useState<string[]>(defaultSelectedKeys);
  const [openKeys, setOpenKeys] = useState<string[]>(defaultOpenKeys);

  const routeMap = useRef<Map<string, React.ReactNode[]>>(new Map());
  const menuMap = useRef<
    Map<string, { menuItem?: boolean; subMenu?: boolean }>
  >(new Map());

  const navbarHeight = 60;
  const menuWidth = collapsed ? 48 : settings.menuWidth;

  const showNavbar = settings.navbar && urlParams.navbar !== false;
  const showMenu = settings.menu && urlParams.menu !== false;
  const showBreadcrumb = urlParams.breadcrumb !== false;
  // 项目详情页不显示 footer
  const isProjectDetailPage = /^\/tasks\/[^/]+$/.test(pathname);
  const showFooter = settings.footer && urlParams.footer !== false && !isProjectDetailPage;

  const flattenRoutes = useMemo(() => getFlattenRoutes(routes) || [], [routes]);

  function renderRoutes(locale) {
    routeMap.current.clear();
    return function travel(_routes: IRoute[], level, parentNode = []) {
      return _routes.map((route) => {
        const { breadcrumb = true, ignore } = route;
        // 只在一级菜单（level === 0）显示图标，二级菜单不显示图标
        const iconDom = level === 0 ? getIconFromKey(route.key) : null;
        const titleDom = (
          <>
            {iconDom} {locale[route.name] || route.name}
          </>
        );

        routeMap.current.set(
          `/${route.key}`,
          breadcrumb ? [...parentNode, route.name] : []
        );

        const visibleChildren = (route.children || []).filter((child) => {
          const { ignore, breadcrumb = true } = child;
          if (ignore || route.ignore) {
            routeMap.current.set(
              `/${child.key}`,
              breadcrumb ? [...parentNode, route.name, child.name] : []
            );
          }

          return !ignore;
        });

        if (ignore) {
          return '';
        }
        if (visibleChildren.length) {
          menuMap.current.set(route.key, { subMenu: true });
          return (
            <SubMenu key={route.key} title={titleDom}>
              {travel(visibleChildren, level + 1, [...parentNode, route.name])}
            </SubMenu>
          );
        }
        menuMap.current.set(route.key, { menuItem: true });
        return <MenuItem key={route.key}>{titleDom}</MenuItem>;
      });
    };
  }

  function onClickMenuItem(key) {
    const currentRoute = flattenRoutes.find((r) => r.key === key);
    const component = currentRoute.component;
    const preload = component.preload();
    NProgress.start();
    preload.then(() => {
      history.push(currentRoute.path ? currentRoute.path : `/${key}`);
      NProgress.done();
    });
  }

  function toggleCollapse() {
    setCollapsed((collapsed) => !collapsed);
  }

  const paddingLeft = showMenu ? { paddingLeft: menuWidth } : {};
  const paddingTop = showNavbar ? { paddingTop: navbarHeight } : {};
  const paddingStyle = { ...paddingLeft, ...paddingTop };

  const updateMenuStatus = useCallback((path: string) => {
    const pathKeys = path.split('/');
    const newSelectedKeys: string[] = [];
    const keysToOpen: string[] = [];
    while (pathKeys.length > 0) {
      const currentRouteKey = pathKeys.join('/');
      const menuKey = currentRouteKey.replace(/^\//, '');
      const menuType = menuMap.current.get(menuKey);
      if (menuType && menuType.menuItem) {
        newSelectedKeys.push(menuKey);
      }
      if (menuType && menuType.subMenu) {
        keysToOpen.push(menuKey);
      }
      pathKeys.pop();
    }
    setSelectedKeys(newSelectedKeys);
    setOpenKeys((prev) => {
      const merged = [...prev];
      let changed = false;
      keysToOpen.forEach((key) => {
        if (!merged.includes(key)) {
          merged.push(key);
          changed = true;
        }
      });
      return changed ? merged : prev;
    });
  }, []);

  useEffect(() => {
    const routeConfig = routeMap.current.get(pathname);
    const nextBreadcrumb =
      routeConfig && routeConfig.length > 0
        ? [...routeConfig]
        : pathname === '/projects/create'
          ? ['menu.projects', 'menu.projects.create']
          : [];

    if (pathname === '/projects/create') {
      const query = qs.parse(search);
      const projectType = query.type;
      const validTypes = ['项目制', '计件制', '离岸制', '驻场制'];

      if (typeof projectType === 'string' && validTypes.includes(projectType)) {
        nextBreadcrumb.push(projectType);
      }
    }

    if (pathname === '/dashboard/hrbp-attendance') {
      nextBreadcrumb.length = 0;
      nextBreadcrumb.push('menu.dashboard.workplace', 'menu.dashboard.hrbpAttendance');
    }
    if (pathname === '/dashboard/resources') {
      nextBreadcrumb.length = 0;
      nextBreadcrumb.push('menu.dashboard.workplace', 'menu.dashboard.resources');
    }

    setBreadCrumb(nextBreadcrumb);
    updateMenuStatus(pathname);
  }, [pathname, search, updateMenuStatus]);

  return (
    <Layout className={styles.layout}>
      <div
        className={cs(styles['layout-navbar'], {
          [styles['layout-navbar-hidden']]: !showNavbar,
        })}
      >
        <Navbar show={showNavbar} />
      </div>
      <Layout>
        {showMenu && (
          <Sider
              className={styles['layout-sider']}
              width={menuWidth}
              collapsed={collapsed}
              onCollapse={setCollapsed}
              trigger={null}
              collapsible
              breakpoint="xl"
              style={paddingTop}
            >
              <div className={styles['menu-wrapper']}>
                <Menu
                  collapse={collapsed}
                  onClickMenuItem={onClickMenuItem}
                  selectedKeys={selectedKeys}
                  openKeys={openKeys}
                  onClickSubMenu={(_, openKeys) => setOpenKeys(openKeys)}
                >
                  {renderRoutes(locale)(routes, 0)}
                </Menu>
              </div>
              <div className={styles['collapse-btn']} onClick={toggleCollapse}>
                {collapsed ? <IconMenuUnfold /> : <IconMenuFold />}
              </div>
            </Sider>
          )}
          <Layout className={styles['layout-content']} style={paddingStyle}>
            <div className={cs({
              [styles['layout-content-wrapper']]: pathname !== '/tasks/calendar', // 全局工时日历页面不应用 padding
            })}>
              {showBreadcrumb && !!breadcrumb.length && (
                <div className={styles['layout-breadcrumb']}>
                  <Breadcrumb>
                    {breadcrumb.map((node, index) => (
                      <Breadcrumb.Item key={index}>
                        {typeof node === 'string' ? locale[node] || node : node}
                      </Breadcrumb.Item>
                    ))}
                  </Breadcrumb>
                </div>
              )}
              <Content>
                <Switch>
                  {/* 客户部设置页 - 必须在 /settings/other 之前 */}
                  <Route
                    exact
                    path="/settings/other/client-departments"
                    component={lazyload(
                      () => import('./pages/settings/other/client-departments')
                    )}
                  />
                  {/* 供应商设置页 - 必须在 /settings/other 之前 */}
                  <Route
                    exact
                    path="/settings/other/suppliers"
                    component={lazyload(
                      () => import('./pages/settings/other/suppliers')
                    )}
                  />
                  {/* 交付阶段设置页 - 必须在 /settings/other 之前 */}
                  <Route
                    exact
                    path="/settings/other/stage-templates"
                    component={lazyload(
                      () => import('./pages/settings/other/stage-templates')
                    )}
                  />
                  {/* 工作日设置页 - 必须在 /settings/other 之前 */}
                  <Route
                    exact
                    path="/settings/other/workday"
                    component={lazyload(
                      () => import('./pages/settings/WorkdaySettings')
                    )}
                  />
                  {/* 任务类型设置页 - 必须在 /settings/other 之前 */}
                  <Route
                    exact
                    path="/settings/other/task-types"
                    component={lazyload(
                      () => import('./pages/settings/other/task-types')
                    )}
                  />
                  {/* 其他任务设置页 - 必须在 /settings/other 之前 */}
                  <Route
                    exact
                    path="/settings/other/other-tasks-config"
                    component={lazyload(
                      () => import('./pages/settings/other/other-tasks-config')
                    )}
                  />
                  {/* 审批流程设置主页面 */}
                  <Route
                    exact
                    path="/settings/approval"
                    component={ApprovalSettingsPage}
                  />
                  {/* 审批流程设置详情页（按类型） */}
                  <Route
                    exact
                    path="/settings/approval/:type"
                    component={ApprovalTypeSettingsPage}
                  />
                  {/* 售前支持列表页 */}
                  <Route
                    exact
                    path="/presales/list"
                    component={lazyload(
                      () => import('./pages/presales/list')
                    )}
                  />
                  {/* 专业贡献列表页 */}
                  <Route
                    exact
                    path="/contributions/list"
                    component={lazyload(
                      () => import('./pages/contributions/list')
                    )}
                  />
                  {/* 人力成本列表页 */}
                  <Route
                    exact
                    path="/costs/labor/list"
                    component={lazyload(
                      () => import('./pages/costs/labor/list')
                    )}
                  />
                  {/* 差旅成本列表页 */}
                  <Route
                    exact
                    path="/costs/travel/list"
                    component={lazyload(
                      () => import('./pages/costs/travel/list')
                    )}
                  />
                  {/* 外包成本列表页 */}
                  <Route
                    exact
                    path="/costs/outsource/list"
                    component={lazyload(
                      () => import('./pages/costs/outsource/list')
                    )}
                  />
                  {/* 任务管理 - 全局工时日历 */}
                  <Route
                    exact
                    path="/tasks/calendar"
                    component={TasksCalendarPage}
                  />
                  {/* 任务管理 - 首页 */}
                  <Route
                    exact
                    path="/tasks/index"
                    component={TasksIndexPage}
                  />
                  {/* 任务管理 - 首页重定向 */}
                  <Route exact path="/tasks">
                    <Redirect to="/tasks/index" />
                  </Route>
                  {/* 任务管理 - 项目详情页 */}
                  <Route
                    exact
                    path="/tasks/:projectId"
                    component={TaskProjectDetailPage}
                  />
                  {/* 审批中心 - 详情页 */}
                  <Route
                    exact
                    path="/approval-center/:id"
                    component={ApprovalCenterDetailPage}
                  />
                  {/* 工时统计报表 - 下钻页（新开页面） */}
                  <Route
                    exact
                    path="/reports/hours-report/drilldown"
                    component={HoursReportDrilldownPage}
                  />
                  {/* 签单业绩报表 - 兼容旧地址 */}
                  <Route exact path="/reports/report2">
                    <Redirect to="/reports/signed-performance" />
                  </Route>
                  {/* HRBP 出勤工时页（隐藏页） */}
                  <Route
                    exact
                    path="/dashboard/hrbp-attendance"
                    component={HrbpAttendancePage}
                  />
                  {/* 资源饱和度页（隐藏页） */}
                  <Route
                    exact
                    path="/dashboard/resources"
                    component={DirectorResourcesPage}
                  />
                  {flattenRoutes.map((route, index) => {
                    return (
                      <Route
                        key={index}
                        path={`/${route.key}`}
                        component={route.component}
                      />
                    );
                  })}
                  {/* 项目创建/编辑页 */}
                  <Route
                    path="/projects/create"
                    component={lazyload(
                      () => import('./pages/projects/create')
                    )}
                  />
                  {/* 项目变更页 */}
                  <Route
                    path="/projects/change"
                    component={lazyload(
                      () => import('./pages/projects/change')
                    )}
                  />
                  {/* 补录申请页 */}
                  <Route
                    path="/projects/pending-entry"
                    component={lazyload(
                      () => import('./pages/projects/pending-entry')
                    )}
                  />
                  {/* 计件项目创建/编辑页 */}
                  <Route
                    path="/frameworks/create"
                    component={lazyload(
                      () => import('./pages/frameworks/create')
                    )}
                  />
                  <Route exact path="/">
                    {defaultRoute ? (
                      <Redirect to={`/${defaultRoute}`} />
                    ) : (
                      <Redirect to="/dashboard/workplace" />
                    )}
                  </Route>
                  <Route
                    path="*"
                    component={lazyload(() => import('./pages/exception/403'))}
                  />
                </Switch>
              </Content>
            </div>
            {showFooter && <Footer />}
          </Layout>
        </Layout>
    </Layout>
  );
}

export default PageLayout;
